import React, { useRef, useState } from 'react';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { ModalHeader, ModalBody, Row, Col, Button } from 'reactstrap';
import Rodal from '../../../components/Rodal/Rodal';
import './FeeCalculatorModal.scss';
import InfoTooltip from '../../../components/info-tooltip/info-tooltip';
import {
    Form,
    Field,
    RadioGroup,
    Radio,
    Label,
    FormGroup,
    Input,
} from '@availity/form';
import * as yup from 'yup';
import { CurrencyInput } from '@availity/form';
import { useStateSelector } from '../../../store/selectors';
import { FeeCalculator } from './FeeCalculator';
import { BrokerageType } from '../../../slices/marketplace/pricing/models/brokerage-type.enum';
import { FeeCalculatedTableValues } from './FeeCalculatedTableValues';
import { FeeCalculatorResultTable } from './FeeCalculatorResultTable/FeeCalculatorResultTable';
import UpgradeSubscriptionModal from '../../../components/UpgadeSubscriptionModal/UpgradeSubscriptionModal';

export interface FeeCalculatorModalProps {
    closeModal: () => void;
    fteW2Dvms: string;
    ltmRevenue: string;
    brokerageType: BrokerageType;
    isVisible: boolean;
    isFreeMember?: boolean;
}

const FeeCalculatorModal = (props: FeeCalculatorModalProps) => {
    const pricing = useStateSelector(
        (state) => state.marketplacePricing.pricing
    );
    const summary = useStateSelector((state) => state.userSummary.summary);
    const [isLtmRevenueTouched, setIsLtmRevenueTouched] = useState(false);
    const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
    const formRef = useRef<any>(null);
    const [resultTableValues, setResultTableValues] =
        useState<FeeCalculatedTableValues>();
    const initialValues = {
        ltmRevenue: props.ltmRevenue?.replace(/,/g, ''),
        fteDvms: props.fteW2Dvms,
        idNdaFee: undefined as number | undefined,
        dataNdaFee: undefined as number | undefined,
        financialModelFee: undefined as number | undefined,
        buyerMeetings: undefined as number | undefined,
        meetingType: 'inPerson',
    };

    useBodyScrollLock();

    const validationSchema = yup.object().shape({
        ltmRevenue: yup
            .number()
            .required('LTM Revenue is required')
            .positive('LTM Revenue must be a positive number'),
        fteDvms: yup
            .number()
            .required('FTE DVMs is required')
            .positive('FTE DVMs must be a positive number'),
        idNdaFee: yup
            .number()
            .required('Buyer ID NDA Fee is required')
            .positive('Buyer ID NDA Fee must be a positive number'),
        dataNdaFee: yup
            .number()
            .required('Data NDA / CIP Fee is required')
            .positive('Data NDA / CIP Fee must be a positive number'),
        financialModelFee: yup
            .number()
            .required('DFM Provision Fee is required')
            .positive('DFM Provision Fee must be a positive number'),
        buyerMeetings: yup
            .number()
            .required('Buyer Meetings is required')
            .positive('Buyer Meetings must be a positive number'),
        meetingType: yup.string().required('Meeting type is required'),
    });

    const resolvedBrokerageType = props.brokerageType;

    const handleSubmit = (values: typeof initialValues) => {
        if (
            resolvedBrokerageType === BrokerageType.FullService &&
            Number(values.fteDvms) < 2
        ) {
            formRef.current?.setFieldError(
                'fteDvms',
                'For Full Service Brokerage, at least 2 FTE DVMs are required'
            );
            return;
        }

        const feeCalculator = new FeeCalculator(pricing);
        const feeTableResult = feeCalculator.CalculateDiscountBrokerageFees({
            brokerageType: resolvedBrokerageType,
            ltmRevenue: Number(values.ltmRevenue),
            fteDvms: Number(values.fteDvms),
            idNdaFee: Number(values.idNdaFee),
            dataNdaFee: Number(values.dataNdaFee),
            financialModelFee: Number(values.financialModelFee),
            buyerMeetings: Number(values.buyerMeetings),
            meetingType: values.meetingType as 'virtual' | 'inPerson',
        });
        setResultTableValues(feeTableResult);
    };

    const preventDecimals = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === '.' || event.key === ',') {
            event.preventDefault();
        }
    };

    const modalTitle = props.isFreeMember
        ? 'Transaction Fee Calculator - For illustrative purposes ONLY'
        : 'Fee Calculator - For illustrative purposes ONLY';

    return (
        <>
            <Rodal
                className="fee-calculator-modal"
                visible={props.isVisible}
                onClose={props.closeModal}
                animation={'fade'}
                showMask={true}>
                <div className="fee-calculator-modal-header">
                    <ModalHeader>
                        {modalTitle}{' '}
                        <InfoTooltip text="We are not representing that this is the appropriate valuation of your practice or that is it what you will actually receive in a sale, these are just example of potential outcomes"></InfoTooltip>
                    </ModalHeader>
                </div>
                <ModalBody className="fee-calculator-modal-body">
                    <Form
                        innerRef={formRef}
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}>
                        {({ values, setFieldValue }) => {
                            return (
                                <div>
                                    <Row>
                                        <Col md={12} className="col-grid">
                                            <FormGroup for="ltmRevenue">
                                                <Label
                                                    for="ltmRevenue"
                                                    className="form-label">
                                                    Practice LTM Revenue
                                                </Label>
                                                <CurrencyInput
                                                    id="ltmRevenue"
                                                    name="ltmRevenue"
                                                    className={`revenue-field form-control ${
                                                        isLtmRevenueTouched
                                                            ? 'input-touched'
                                                            : ''
                                                    }`}
                                                    value={values.ltmRevenue}
                                                    prefix="$"
                                                    decimalsLimit={2}
                                                    onBlur={() =>
                                                        setIsLtmRevenueTouched(
                                                            true
                                                        )
                                                    }
                                                    onValueChanged={(e) => {
                                                        setFieldValue(
                                                            'ltmRevenue',
                                                            e
                                                        );
                                                    }}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={12} className="col-grid">
                                            <FormGroup for="fteDvms">
                                                <Label>
                                                    Practice FTE DVMs{' '}
                                                    <InfoTooltip text="FTE DVM = 36 clinical hours per week. For example, a DVM working 18 hours per week would be 0.5 FTE DVMS."></InfoTooltip>
                                                </Label>
                                                <Input
                                                    name="fteDvms"
                                                    type="number"
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={12} className="col-grid">
                                            <Field
                                                name="idNdaFee"
                                                label="# of buyers signing an ID NDA"
                                                type="number"
                                                className="form-control"
                                                onKeyDown={preventDecimals}
                                            />
                                        </Col>
                                        <Col md={12} className="col-grid">
                                            <Field
                                                name="dataNdaFee"
                                                label="# of buyers signing a Data NDA / Receiving PIP"
                                                type="number"
                                                className="form-control"
                                                onKeyDown={preventDecimals}
                                            />
                                        </Col>
                                        <Col md={12} className="col-grid">
                                            <Field
                                                name="financialModelFee"
                                                label="# of buyer receiving financial model"
                                                type="number"
                                                className="form-control"
                                                onKeyDown={preventDecimals}
                                            />
                                        </Col>
                                        <Col md={12} className="col-grid">
                                            <Field
                                                name="buyerMeetings"
                                                label="# of buyers attending meetings"
                                                type="number"
                                                className="form-control"
                                                onKeyDown={preventDecimals}
                                            />
                                        </Col>
                                        <Col
                                            md={12}
                                            className="meetings-section col-grid">
                                            <Label className="center-label">
                                                Buyer Meeting Type
                                            </Label>
                                            <RadioGroup
                                                name="meetingType"
                                                className="meetings-radio-group"
                                                label=" "
                                                onChange={(e) => {}}>
                                                <Col md={12}>
                                                    <Radio
                                                        value="virtual"
                                                        label="Virtual Buyer Meetings"
                                                    />
                                                </Col>
                                                <Col md={12}>
                                                    <Radio
                                                        value="inPerson"
                                                        label="In-Person Buyer Meetings"
                                                    />
                                                </Col>
                                            </RadioGroup>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={12} className="text-center">
                                            <Button
                                                type="submit"
                                                color="primary"
                                                onClick={() => {
                                                    setIsLtmRevenueTouched(
                                                        true
                                                    );
                                                }}>
                                                Calculate
                                            </Button>
                                        </Col>
                                    </Row>
                                </div>
                            );
                        }}
                    </Form>
                    {resultTableValues && (
                        <>
                            <FeeCalculatorResultTable
                                brokerageType={resolvedBrokerageType}
                                table={resultTableValues}
                                isFreeMember={props.isFreeMember}
                            />
                            {props.isFreeMember && (
                                <div className="text-center mt-3">
                                    <button
                                        className="btn btn-primary"
                                        onClick={() =>
                                            setIsUpgradeModalOpen(true)
                                        }>
                                        Become a paying member
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </ModalBody>
            </Rodal>
            {isUpgradeModalOpen && (
                <UpgradeSubscriptionModal
                    isVisible={isUpgradeModalOpen}
                    onClose={() => setIsUpgradeModalOpen(false)}
                    ownerType={summary?.practiceOwnerType}
                />
            )}
        </>
    );
};

export default FeeCalculatorModal;
